import { ReactNode, useEffect, useRef, useState } from "react";
import LiveChoiceButtons from './LiveChoiceButtons';
import { createPortal } from "react-dom";
import { AdminActionButton } from "../../../../Component/UI/AdminPageHeader";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import api from "../../../../Component/axios_Interceptor/api";
import api2 from "../../../../Component/axios_Interceptor/api2";
import { StoreInfo } from "../../../../Component/Interface/Store";
import { fontStyles } from "../../../../Component/style/admin_font";
import url, { SERVER_ADDRESS } from "../../../../Component/server";
import SendPaymentModal from "../Modal/SendPaymentModal";
import LiveDetailDialog from "./LiveDetailDialog";
import LiveToolbarIcon from "./LiveToolbarIcon";

type Participant = {
  user_seq: number;
  nickname: string;
  name: string;
  ticket_count: number;
  individual_granted?: boolean | number;
};

const useNotificationProgress = (seq: string | undefined, kind: "start" | "statement") => {
  const [dispatched, setDispatched] = useState(false);
  const [counts, setCounts] = useState<{ delivered: number; total: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    setDispatched(false);
    setCounts(null);
    setLoading(true);
    setError("");
    const refresh = async () => {
      let refreshAgain = true;
      try {
        const { data } = await api.get(`${SERVER_ADDRESS}notifications/preview/${seq}`, { params: { kind } });
        if (!active) return;
        if (!data || !(data.dispatch === null || Number.isSafeInteger(data.dispatch?.dispatch_id))) {
          throw new Error("Invalid statement response");
        }
        const dispatch = data.dispatch;
        const total = dispatch ? dispatch.total : data.recipient_count;
        const delivered = dispatch ? dispatch.delivered_cnt : 0;
        if (!Number.isSafeInteger(total) || total < 0 || !Number.isSafeInteger(delivered) || delivered < 0 || delivered > total) {
          throw new Error("Invalid statement counts");
        }
        // An older in-flight preview must not unlock a newly accepted dispatch.
        setDispatched(sent => sent || Boolean(dispatch));
        setCounts({ delivered, total });
        setError("");
        refreshAgain = !dispatch || dispatch.pending_cnt > 0 || dispatch.send_cnt > delivered || dispatch.unknown_cnt > 0;
      } catch {
        if (active) setError(kind === "statement" ? "정산서 발송 내역을 확인하지 못했습니다. 잠시 후 다시 확인합니다." : "시작톡 발송 내역을 확인하지 못했습니다. 잠시 후 다시 확인합니다.");
      } finally {
        if (active) {
          setLoading(false);
          if (refreshAgain) timer = setTimeout(refresh, 5000);
        }
      }
    };
    void refresh();
    return () => { active = false; clearTimeout(timer); };
  }, [seq, kind]);

  return { dispatched, setDispatched, counts, loading, error };
};

const LiveOperationsPanel = ({ storeInfo, liveStatus: currentLiveStatus, couponActionTarget }: { storeInfo: StoreInfo; liveStatus?: string; couponActionTarget?: HTMLElement | null }) => {
  const { seq } = useParams();
  const [sendType, setSendType] = useState("");
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [historyOnly, setHistoryOnly] = useState(false);
  const { dispatched: statementSent, setDispatched: setStatementSent, counts: statementCounts,
    loading: statementLoading, error: statementError } = useNotificationProgress(seq, "statement");
  const { counts: startCounts, error: startError } = useNotificationProgress(seq, "start");
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [cancelTicket, setCancelTicket] = useState(0);
  const [loadedLiveStatus, setLiveStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [participantSearch, setParticipantSearch] = useState("");
  const [participantLoading, setParticipantLoading] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(0);
  const [grantCount, setGrantCount] = useState(1);
  const grantInFlight = useRef(false);
  const grantRequest = useRef<{ fingerprint: string; id: string } | null>(null);
  const [grantBusy, setGrantBusy] = useState(false);
  const [grantMessage, setGrantMessage] = useState("");
  const [grantError, setGrantError] = useState("");
  const participantRequest = useRef(0);
  const liveStatus = currentLiveStatus ?? loadedLiveStatus;
  const actionsDisabled = !['예약', '리허설', '방송중'].includes(liveStatus);
  const statementDisabled = !['방송중', '종료'].includes(liveStatus);
  const sendDisabled = sendType === '정산서' ? statementDisabled : actionsDisabled;

  useEffect(() => {
    if (sendDisabled && !historyOnly) setSendModalOpen(false);
    if (actionsDisabled) setCouponModalOpen(false);
  }, [actionsDisabled, sendDisabled, historyOnly]);


  const loadParticipants = async (search = "") => {
    if (!seq) return;
    const requestId = ++participantRequest.current;
    setParticipantLoading(true);
    setGrantError("");
    try {
      const { data } = await api.get(url.Communication_url.cancel_ticket_participants, {
        params: { live_seq: Number(seq), search },
      });
      if (requestId !== participantRequest.current) return;
      if (!Array.isArray(data)) throw new Error("Invalid participants response");
      setParticipants(data);
      setSelectedParticipant(current => data.some((row: Participant) => Number(row.user_seq) === current)
        ? current
        : Number(data[0]?.user_seq ?? 0));
    } catch (failure: any) {
      if (requestId !== participantRequest.current) return;
      setParticipants([]);
      setSelectedParticipant(0);
      setGrantError(failure?.response?.data?.message || "참여 고객을 불러오지 못했습니다.");
    } finally {
      if (requestId === participantRequest.current) setParticipantLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    setCancelTicket(0);
    setLiveStatus("");
    setParticipants([]);
    setSelectedParticipant(0);
    participantRequest.current += 1;

    api2.get(url.Communication_url.get_live_info, { params: { seq } })
      .then(({ data }) => {
        if (!active || !data || typeof data !== "object") return;
        const status = typeof data.live_status === "string" ? data.live_status : "";
        setCancelTicket(Number.isFinite(Number(data.cancel_ticket)) ? Number(data.cancel_ticket) : 0);
        setLiveStatus(status);
        if (status === "방송중") void loadParticipants();
      })
      .catch((failure: any) => {
        if (active) setLoadError(failure?.response?.data?.message || "방송 운영 정보를 불러오지 못했습니다.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      participantRequest.current += 1;
    };
  }, [seq]);

  const openSendModal = (type: string) => {
    if (type === '정산서'
      ? statementDisabled || statementLoading || statementSent || Boolean(statementError)
      : actionsDisabled) return;
    setHistoryOnly(false);
    setSendType(type);
    setSendModalOpen(true);
  };

  const grantCancelTicket = async () => {
    if (actionsDisabled || liveStatus !== '방송중' || grantBusy || grantInFlight.current || !seq || !selectedParticipant || grantCount < 1) return;
    const fingerprint = JSON.stringify([seq, selectedParticipant, grantCount]);
    if (grantRequest.current?.fingerprint !== fingerprint) grantRequest.current = { fingerprint, id: crypto.randomUUID() };
    grantInFlight.current = true;
    setGrantBusy(true);
    setGrantMessage("");
    setGrantError("");
    try {
      const { data } = await api.patch(url.Communication_url.grant_cancel_ticket, {
        request_id: grantRequest.current.id,
        live_seq: Number(seq),
        user_seq: selectedParticipant,
        ticket_count: grantCount,
      });
      if (!Number.isSafeInteger(data?.ticket_count) || data.ticket_count < 0) throw new Error('쿠폰 지급 결과를 확인하지 못했습니다. 같은 수량으로 다시 시도해주세요.');
      setParticipants(current => current.map(participant => participant.user_seq === selectedParticipant
        ? { ...participant, ticket_count: Number(data.ticket_count), individual_granted: true }
        : participant));
      grantRequest.current = null;
      setGrantMessage(`취소 쿠폰을 지급했습니다. 현재 ${Number(data.ticket_count).toLocaleString()}장`);
      setCouponModalOpen(false);
    } catch (failure: any) {
      setGrantError(failure?.response?.data?.message || "취소 쿠폰을 지급하지 못했습니다.");
    } finally {
      grantInFlight.current = false;
      setGrantBusy(false);
    }
  };

  const grantAllCancelTickets = async () => {
    if (actionsDisabled || liveStatus !== '방송중' || grantBusy || !seq || grantCount < 1) return;
    if (!window.confirm(`지급 시점에 라이브에 접속 중인 회원에게만 취소 쿠폰 ${grantCount}장씩 지급하시겠습니까?`)) return;
    setGrantBusy(true);
    setGrantMessage("");
    setGrantError("");
    try {
      const { data } = await api.patch(url.Communication_url.grant_cancel_ticket_all, {
        live_seq: Number(seq),
        ticket_count: grantCount,
      });
      if (!Number.isSafeInteger(data?.participant_count) || data.participant_count < 0) throw new Error('쿠폰 지급 결과를 확인하지 못했습니다. 다시 확인해주세요.');
      await loadParticipants(participantSearch);
      setGrantMessage(`접속 회원 ${Number(data.participant_count).toLocaleString()}명에게 취소 쿠폰 ${grantCount}장씩 지급했습니다.`);
      setCouponModalOpen(false);
    } catch (failure: any) {
      setGrantError(failure?.response?.data?.message || "전체 취소 쿠폰을 지급하지 못했습니다.");
    } finally {
      setGrantBusy(false);
    }
  };

  const couponPortal = (content: ReactNode) => couponActionTarget ? createPortal(content, couponActionTarget.ownerDocument.body) : content;
  return (
    <Panel aria-label="방송 운영 도구">
      <PanelHeading>
        <strong>방송 운영 도구</strong>
      </PanelHeading>

      <ActionGrid>
        <PrimaryButton type="button" disabled={actionsDisabled} onClick={() => openSendModal("알림톡")}>
          시작톡 보내기
          {startCounts && <NotificationCount title="성공/팔로워 수" aria-label={`성공 ${startCounts.delivered}건 / 팔로워 ${startCounts.total}명`}>
            ({startCounts.delivered}/{startCounts.total})
          </NotificationCount>}
        </PrimaryButton>
        <ActionButton type="button" disabled={statementDisabled || statementLoading || statementSent || Boolean(statementError)} onClick={() => openSendModal("정산서")}>
          {statementSent ? "정산서 발송 접수됨" : statementLoading ? "정산서 확인 중…" : "정산서 보내기"}
          {statementCounts && <NotificationCount title="성공/정산서 총수" aria-label={`성공 ${statementCounts.delivered}건 / 정산서 총 ${statementCounts.total}건`}>
            ({statementCounts.delivered}/{statementCounts.total})
          </NotificationCount>}
        </ActionButton>
        {couponActionTarget === undefined ? <CouponButton type="button" onClick={() => { setCouponModalOpen(true); void loadParticipants(participantSearch); }} disabled={actionsDisabled || loading || Boolean(loadError)}>
          취소 쿠폰 지급·관리
        </CouponButton> : couponActionTarget && createPortal(<AdminActionButton $accented type="button" aria-haspopup="dialog"
          disabled={actionsDisabled || loading || Boolean(loadError)} onClick={() => { setCouponModalOpen(true); void loadParticipants(participantSearch); }}><LiveToolbarIcon name="cancelCoupon" />취소 쿠폰</AdminActionButton>, couponActionTarget)}
      </ActionGrid>

      {statementSent && <Feedback role="status">
        정산서는 한 번만 발송할 수 있습니다. <HistoryButton type="button" onClick={() => { setHistoryOnly(true); setSendType("정산서"); setSendModalOpen(true); }}>발송 내역 보기</HistoryButton>
      </Feedback>}
      {statementError && <Feedback role="alert">{statementError}</Feedback>}
      {startError && <Feedback role="alert">{startError}</Feedback>}

      {loadError && <Feedback role="alert">{loadError}</Feedback>}

      {couponPortal(<LiveDetailDialog
        open={couponModalOpen && !actionsDisabled}
        onClose={() => setCouponModalOpen(false)}
        title="취소 쿠폰 지급·관리"
        description="라이브 참여 고객에게 취소 쿠폰을 추가로 지급할 수 있습니다."
        size="large"
      >
        <CouponOverview>
          <div>
            <span>라이브 생성 시 설정한 기본 쿠폰</span>
            <strong>{loading ? "확인 중…" : `${cancelTicket.toLocaleString()}장`}</strong>
          </div>
          <p>기본 쿠폰 수량은 라이브 생성 후 수정할 수 없습니다.</p>
        </CouponOverview>

        {loadError ? (
          <Feedback role="alert">{loadError}</Feedback>
        ) : liveStatus === "방송중" ? (
          <GrantCouponSection aria-label="참여 고객 취소 쿠폰 지급">
            <strong>접속 중인 회원에게 추가 지급</strong>
            <p>일반 회원과 셀러 모두에게 취소 쿠폰을 지급할 수 있습니다.</p>
            <GrantSearchRow data-admin-search="">
              <input
                value={participantSearch}
                onChange={event => setParticipantSearch(event.target.value)}
                placeholder="고객 이름 또는 닉네임"
                aria-label="참여 고객 검색"
              />
              <button data-admin-search-action="" type="button" disabled={participantLoading} onClick={() => loadParticipants(participantSearch)}>
                {participantLoading ? "검색 중…" : "검색"}
              </button>
            </GrantSearchRow>
            {participantLoading ? <p role="status">참여 고객을 불러오는 중입니다.</p> : (
              <GrantRow>
                <LiveChoiceButtons label="취소 쿠폰 지급 고객" value={String(selectedParticipant)} searchable
                  onChange={value => setSelectedParticipant(Number(value))}
                  options={participants.map(participant => ({ value: String(participant.user_seq),
                    label: `${participant.nickname} (${participant.name}) · 현재 ${participant.ticket_count}장${participant.individual_granted ? ' · 이전 지급 있음' : ''}` }))} />
                <input
                  aria-label="추가 지급 수량"
                  type="number"
                  min={1}
                  max={100}
                  value={grantCount}
                  onChange={event => setGrantCount(Math.min(100, Math.max(1, Math.floor(Number(event.target.value) || 1))))}
                />
              </GrantRow>
            )}
            <GrantButtons>
                <button type="button" disabled={grantBusy || selectedParticipant === 0} onClick={grantCancelTicket}>
                  {grantBusy ? "지급 중…" : "선택 고객 지급"}
                </button>
                <button type="button" disabled={grantBusy} onClick={grantAllCancelTickets}>
                  {grantBusy ? "지급 중…" : "접속 회원 전체 지급"}
                </button>
            </GrantButtons>
            {grantMessage && <p role="status">{grantMessage}</p>}
            {grantError && <p role="alert">{grantError}</p>}
          </GrantCouponSection>
        ) : (
          <CouponNotice>참여 고객 추가 지급은 방송 중인 라이브에서 사용할 수 있습니다.</CouponNotice>
        )}
      </LiveDetailDialog>)}

      {(!sendDisabled || historyOnly) && <SendPaymentModal
        visible={sendModalOpen}
        handleModal={() => setSendModalOpen(false)}
        sendType={sendType}
        readOnly={historyOnly}
        store_info={storeInfo}
        onDispatched={() => { if (sendType === "정산서") setStatementSent(true); }}
      />}
    </Panel>
  );
};

const GrantButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-top: var(--space-4);

  button:last-child:not(:disabled) {
    border-color: var(--color-border-strong);
    background: var(--color-bg-surface);
    color: var(--color-text-primary);
  }
  button:last-child:hover:not(:disabled) {
    border-color: var(--color-brand);
    background: var(--color-brand-subtle);
    color: var(--color-text-link);
  }
`;

export default LiveOperationsPanel;

const Panel = styled.section`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 16px;
  border: 1px solid var(--admin-theme-border, #e9e6ef);
  border-radius: 12px;
  background: var(--admin-theme-surface, #fff);
`;

const PanelHeading = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;

  strong { display: block; color: var(--admin-theme-text, #2d2735); font-size: 16px; ${fontStyles.Bold} }
`;
const ActionGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  > button { flex: 1 1 140px; min-width: 0; }
`;

const ActionButton = styled.button`
  min-height: 42px;
  padding: 0 10px;
  border: 1px solid var(--admin-theme-border, #d8d0e8);
  border-radius: 10px;
  background: var(--admin-theme-surface, #ffffff);
  color: var(--admin-theme-text, #3a3345);
  ${fontStyles.semiBold}
  font-size: 13px;
  cursor: pointer;

  &:hover:not(:disabled) { border-color: #8464dd; color: var(--admin-theme-link, #5730c4); background: var(--admin-theme-surface, #ffffff); }
  &:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
  &:disabled { cursor: not-allowed; opacity: .55; }
`;

const HistoryButton = styled.button`
  min-height: 44px;
  padding: 0 8px;
  border: 0;
  background: transparent;
  color: var(--admin-theme-link, #5730c4);
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
  &:disabled { cursor: not-allowed; opacity: .55; }
  &:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
`;

const NotificationCount = styled.span`
  display: inline-block;
  margin-left: 6px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const PrimaryButton = styled(ActionButton)`
  border-color: #6338dc;
  background: #6338dc;
  color: #ffffff;
  &:hover:not(:disabled) { border-color: #5027c0; background: #5027c0; color: #ffffff; }
`;

const CouponButton = styled(ActionButton)`
  border-color: #d6a52d;
  background: var(--admin-theme-warning-soft, #fffaf0);
  color: var(--admin-theme-warning, #77530b);
  &:hover:not(:disabled) { border-color: #b98713; background: var(--admin-theme-warning-soft, #fff6df); color: var(--admin-theme-warning, #604100); }
`;

const Feedback = styled.div`
  margin-top: 12px;
  color: var(--admin-theme-success, #246b36);
  font-size: 14px;
  line-height: 1.5;
  &[role="alert"] { color: var(--admin-theme-danger, #b42318); }

  input {
    display: block;
    box-sizing: border-box;
    width: min(100%, 36rem);
    min-height: 44px;
    margin-top: 8px;
    padding: 10px 12px;
    border: 1px solid var(--admin-theme-border, #c6c6c6);
    border-radius: 8px;
    background: var(--admin-theme-surface, #ffffff);
    color: var(--admin-ink);
  }
`;

const CouponOverview = styled.section`
  padding: 18px;
  border: 1px solid var(--admin-theme-border, #e7e1f4);
  border-radius: 12px;
  background: var(--admin-theme-raised, #faf8ff);

  div { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  span { color: var(--admin-theme-muted, #5f5868); font-size: 14px; }
  strong { color: var(--admin-theme-link, #4f27bd); font-size: 22px; ${fontStyles.Bold} }
  p { margin: 8px 0 0; color: var(--admin-theme-muted, #817a89); font-size: 13px; }
`;

const GrantCouponSection = styled.section`
  margin-top: 16px;
  padding: 18px;
  border: 1px solid var(--admin-theme-border, #e5e0f5);
  border-radius: 12px;
  background: var(--admin-theme-surface, #ffffff);

  > strong { display: block; margin-bottom: 12px; color: var(--admin-ink); }
  input, select, button { box-sizing: border-box; min-height: 44px; border-radius: 8px; font-size: 14px; }
  input, select { min-width: 0; padding: 10px 12px; border: 1px solid var(--admin-theme-border, #d5d0df); background: var(--admin-theme-surface, #ffffff); }
  button {
    padding: 0 var(--space-4);
    border: 1px solid var(--color-brand);
    background: var(--gradient-brand);
    color: var(--color-text-on-brand);
    cursor: pointer;
    transition: background var(--duration-fast), color var(--duration-fast), border-color var(--duration-fast);
  }
  button:hover:not(:disabled) {
    border-color: var(--color-brand-hover);
    background: var(--gradient-brand-hover);
    color: var(--color-text-on-brand);
  }
  button:disabled {
    border-color: var(--color-border-default);
    background: var(--color-bg-muted);
    color: var(--color-text-disabled);
    cursor: not-allowed;
  }
  button:focus-visible, input:focus-visible, select:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
  p { margin: 10px 0 0; color: var(--admin-theme-text, #555555); }
  [role="alert"] { color: var(--admin-theme-danger, #bd0030); }
`;

const GrantSearchRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  input { flex: 1; }
`;

const GrantRow = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 1fr) 90px auto auto;
  gap: 8px;

  @media (max-width: 820px) { grid-template-columns: 1fr 90px; }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const CouponNotice = styled.p`
  margin: 16px 0 0;
  padding: 14px 16px;
  border-radius: 10px;
  background: var(--admin-theme-raised, #f4f2f6);
  color: var(--admin-theme-muted, #5f5868);
  font-size: 14px;
  line-height: 1.5;
`;
